// App-switcher cover (Tool Plan §3 rule 7, D-67, IR-08; I-1a batch 5, IP2-32 (c)).
//
// The phone's app switcher shows a snapshot of the last frame. To keep it
// neutral, a full-screen plain layer goes up as soon as the app may be leaving:
// on `blur` (on Android this fires first, before the snapshot — I-0a A9), on
// `visibilitychange` to hidden, and on `pagehide`. It comes down when the
// person is back: `focus`, or visible again with focus, or `pageshow` with focus.
// A tap on the layer also removes it, so it can never trap anyone.
//
// The cover is switched SYNCHRONOUSLY inside the event handler, as a class on
// <html> (COVER_CLASS): the CSS shows the layer and hides the page in the same
// task, before the browser paints the frame the snapshot is taken from. A React
// state update would paint a frame later — on the S25 Ultra that let the
// snapshot catch the page (Deploy #4 check S10; I-0a A9 passed this way).
//
// A tap that hands off to another app (Call / Message / chat on Help now) covers
// in the tap itself (`coverForHandoff`): opening the dialer snapshots the page
// BEFORE `blur` fires (Deploy #5 check S10). If nothing opened, a safety timer
// takes the cover down again once the page is still visible and focused.
//
// The cover hides; it does not lock. Background auto-lock (>= 30 s) is separate
// (app/lock/autoLock.ts) and unchanged.
//
// The event targets, the root element and the timer are parameters so tests
// drive them without a browser.

import { useCallback, useEffect } from 'react'

export const COVER_CLASS = 'app-covered'

/** How long a tap-set cover waits before it comes down if nothing opened. */
export const HANDOFF_SAFETY_MS = 1000

export interface CoverEnv {
  /** Fires blur / focus / pagehide / pageshow (the window). */
  readonly win: EventTarget
  /** Fires visibilitychange (the document). */
  readonly doc: EventTarget & { readonly visibilityState: DocumentVisibilityState; hasFocus(): boolean }
  /** Carries the cover class (<html>). */
  readonly root: { readonly classList: Pick<DOMTokenList, 'add' | 'remove'> }
}

/** Runs `run` once after `ms` milliseconds. */
export type Schedule = (run: () => void, ms: number) => void

function browserEnv(): CoverEnv {
  return { win: window, doc: document, root: document.documentElement }
}

function browserSchedule(run: () => void, ms: number): void {
  window.setTimeout(run, ms)
}

function isBack(doc: CoverEnv['doc']): boolean {
  return doc.visibilityState === 'visible' && doc.hasFocus()
}

/** Start watching. Returns a stop function (which also removes the cover). */
export function startAppSwitcherCover(env: CoverEnv = browserEnv()): () => void {
  const cover = (): void => env.root.classList.add(COVER_CLASS)
  const uncover = (): void => env.root.classList.remove(COVER_CLASS)
  const uncoverIfBack = (): void => {
    if (isBack(env.doc)) {
      uncover()
    }
  }
  const onVisibility = (): void => {
    if (env.doc.visibilityState === 'hidden') {
      cover()
    } else {
      uncoverIfBack()
    }
  }

  env.win.addEventListener('blur', cover)
  env.win.addEventListener('pagehide', cover)
  env.win.addEventListener('focus', uncover)
  env.win.addEventListener('pageshow', uncoverIfBack)
  env.doc.addEventListener('visibilitychange', onVisibility)
  return () => {
    env.win.removeEventListener('blur', cover)
    env.win.removeEventListener('pagehide', cover)
    env.win.removeEventListener('focus', uncover)
    env.win.removeEventListener('pageshow', uncoverIfBack)
    env.doc.removeEventListener('visibilitychange', onVisibility)
    uncover()
  }
}

/**
 * Cover NOW, inside the click on a link that opens another app, before the
 * browser hands off. The same `focus` / visible-with-focus that ends any cover
 * ends this one on return; the safety timer ends it if nothing opened.
 */
export function coverForHandoff(env: CoverEnv = browserEnv(), schedule: Schedule = browserSchedule): void {
  env.root.classList.add(COVER_CLASS)
  schedule(() => {
    if (isBack(env.doc)) {
      env.root.classList.remove(COVER_CLASS)
    }
  }, HANDOFF_SAFETY_MS)
}

/** Watches while mounted; returns `remove` for a tap on the cover itself. */
export function useAppSwitcherCover(): () => void {
  useEffect(() => startAppSwitcherCover(), [])
  return useCallback(() => document.documentElement.classList.remove(COVER_CLASS), [])
}
